/**
 * useApiLayers Hook
 * Manages API-based map layers using React Query.
 * Fetches data via React Query and renders layers when data is available,
 * waiting for the map style to be fully ready. This keeps behavior
 * consistent with vessel rendering, especially on reloads.
 *
 * Key resilience features:
 * - Detects style reloads via layerStatus reset and re-renders layers.
 * - Uses withMapLayerRetry for automatic retries.
 */
import { useEffect, useRef } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { useMilitaryLocationsQuery } from '@/hooks/useMilitaryLocationsQuery';
import { usePolygonIncidents } from '@/hooks/usePolygonIncidents';
import { usePolygonPorts } from '@/hooks/usePolygonPorts';
import { addIncidentsLayer } from '@/services/map/layers/incidentsLayer';
import { addMilitaryLocationsLayer } from '@/services/map/layers/militaryLocationsLayer';
import { addPortsLayer } from '@/services/map/layers/portsLayer';
import { withMapLayerRetry } from '@/services/map/mapReadiness';
import { useMapStore } from '@/stores/slices/useMapStore';

interface UseApiLayersOptions {
  getToken: (() => Promise<string>) | undefined;
  polygonWkt: string | null;
}

/**
 * Hook for managing API-based map layers
 * Uses React Query for data fetching and caching
 * Renders layers when data is available and map is ready
 */
export function useApiLayers({ getToken, polygonWkt }: UseApiLayersOptions) {
  const { map, isMapReady } = useMapbox();

  // Subscribe to layer status to detect style reloads
  const incidentsStatus = useMapStore((state) => state.layerStatus.incidents?.status);
  const portsStatus = useMapStore((state) => state.layerStatus.ports?.status);
  const militaryStatus = useMapStore((state) => state.layerStatus['military-locations']?.status);

  // Track which layers have been rendered to avoid duplicate renders
  const incidentsRenderedRef = useRef<string | null>(null);
  const portsRenderedRef = useRef<string | null>(null);
  const militaryRenderedRef = useRef<string | null>(null);

  // Fetch data via React Query
  const incidentsQuery = usePolygonIncidents(getToken, polygonWkt);
  const portsQuery = usePolygonPorts(getToken, polygonWkt);
  const militaryQuery = useMilitaryLocationsQuery(getToken, polygonWkt);

  // Render incidents layer when data is available
  // Also re-render if status was reset (style reload)
  useEffect(() => {
    if (!isMapReady || !map.current) return;
    if (!incidentsQuery.data) return;

    const mapInstance = map.current;

    // Build cache key; include status to detect resets
    const cacheKey = `${polygonWkt}-${incidentsQuery.data.length}-${incidentsQuery.dataUpdatedAt}`;

    // If status was reset to idle, invalidate cache so we re-render
    if (incidentsStatus === 'idle' && incidentsRenderedRef.current) {
      incidentsRenderedRef.current = null;
    }

    // Skip if already rendered for this data set
    if (incidentsRenderedRef.current === cacheKey) return;

    let cancelled = false;

    const renderIncidents = async () => {
      if (cancelled) return;

      const result = await withMapLayerRetry({
        layerKey: 'incidents',
        map: mapInstance,
        attempt: async (m) => {
          if (!incidentsQuery.data) return;
          addIncidentsLayer(m, incidentsQuery.data);
          incidentsRenderedRef.current = cacheKey;
        },
      });

      if (!result.success) {
        console.error('useApiLayers: Failed to render incidents layer', result.error);
      }
    };

    void renderIncidents();

    return () => {
      cancelled = true;
    };
  }, [isMapReady, map, incidentsQuery.data, incidentsQuery.dataUpdatedAt, polygonWkt, incidentsStatus]);

  // Render ports layer when data is available
  // Ports are global data, so we render them as soon as they're fetched
  // Also re-render if status was reset (style reload)
  useEffect(() => {
    if (!isMapReady || !map.current) return;
    if (!portsQuery.data || portsQuery.data.length === 0) return;

    const mapInstance = map.current;

    // Build cache key
    const cacheKey = `ports-${portsQuery.data.length}-${portsQuery.dataUpdatedAt}`;

    // If status was reset to idle, invalidate cache so we re-render
    if (portsStatus === 'idle' && portsRenderedRef.current) {
      portsRenderedRef.current = null;
    }

    // Skip if already rendered with same data
    if (portsRenderedRef.current === cacheKey) return;

    let cancelled = false;

    const renderPorts = async () => {
      if (cancelled) return;

      const result = await withMapLayerRetry({
        layerKey: 'ports',
        map: mapInstance,
        attempt: async (m) => {
          if (!portsQuery.data || portsQuery.data.length === 0) return;
          addPortsLayer(m, portsQuery.data);
          portsRenderedRef.current = cacheKey;
        },
      });

      if (!result.success) {
        console.error('useApiLayers: Failed to render ports layer', result.error);
      }
    };

    void renderPorts();

    return () => {
      cancelled = true;
    };
  }, [isMapReady, map, portsQuery.data, portsQuery.dataUpdatedAt, portsStatus]);

  // Render military locations layer when data is available
  // Also re-render if status was reset (style reload)
  useEffect(() => {
    if (!isMapReady || !map.current) return;
    if (!militaryQuery.data) return;

    const mapInstance = map.current;

    // Build cache key
    const cacheKey = `${polygonWkt}-${militaryQuery.data.length}-${militaryQuery.dataUpdatedAt}`;

    // If status was reset to idle, invalidate cache so we re-render
    if (militaryStatus === 'idle' && militaryRenderedRef.current) {
      militaryRenderedRef.current = null;
    }

    // Skip if already rendered for this polygon
    if (militaryRenderedRef.current === cacheKey) return;

    let cancelled = false;

    const renderMilitary = async () => {
      if (cancelled) return;

      const result = await withMapLayerRetry({
        layerKey: 'military-locations',
        map: mapInstance,
        attempt: async (m) => {
          if (!militaryQuery.data) return;
          addMilitaryLocationsLayer(m, militaryQuery.data);
          militaryRenderedRef.current = cacheKey;
        },
      });

      if (!result.success) {
        console.error('useApiLayers: Failed to render military locations layer', result.error);
      }
    };

    void renderMilitary();

    return () => {
      cancelled = true;
    };
  }, [isMapReady, map, militaryQuery.data, militaryQuery.dataUpdatedAt, polygonWkt, militaryStatus]);

  return {
    incidentsLoading: incidentsQuery.isLoading,
    portsLoading: portsQuery.isLoading,
    militaryLoading: militaryQuery.isLoading,
    incidentsError: incidentsQuery.error,
    portsError: portsQuery.error,
    militaryError: militaryQuery.error,
  };
}
